import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';


const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const LS_CACHE_KEY = 'rivnus_exchange_rates';

// ─── ExchangeRate-API (open.er-api.com) ──────────────────────────────────────
// Free, no API key required, CORS-enabled, updated daily.
// Returns rates as "1 USD = X of currency"; we invert to store as
// "1 of currency = X USD" so the rest of the conversion logic is unchanged.

async function fetchRates() {
  const res = await fetch('https://open.er-api.com/v6/latest/USD');
  if (!res.ok) throw new Error(`ExchangeRate-API responded ${res.status}`);

  const json = await res.json();
  if (json.result !== 'success') throw new Error(`ExchangeRate-API error: ${json['error-type']}`);

  // json.rates["EUR"] = 0.92 → "1 USD = 0.92 EUR"
  // We store usdRates["EUR"] = 1/0.92 = 1.087 → "1 EUR = 1.087 USD"
  const usdRates = { USD: 1.0 };
  for (const [currency, rate] of Object.entries(json.rates)) {
    if (rate > 0) usdRates[currency] = 1 / rate;
  }
  return usdRates;
}

// ─── Firestore cache (shared across all users) ───────────────────────────────
// Requires Firestore rule:
//   match /exchangeRates/{doc} {
//     allow read: if true;
//     allow write: if request.auth != null;
//   }

const firestoreRef = () => doc(db, 'exchangeRates', 'global');

async function loadFirestoreCache() {
  try {
    const snap = await getDoc(firestoreRef());
    if (!snap.exists()) return null;
    const { usdRates, fetchedAt } = snap.data();
    const age = Date.now() - (fetchedAt?.toMillis?.() ?? 0);
    return age < CACHE_TTL_MS ? usdRates : null;
  } catch {
    return null;
  }
}

async function saveFirestoreCache(usdRates) {
  try {
    await setDoc(firestoreRef(), { usdRates, fetchedAt: serverTimestamp() });
  } catch (err) {
    console.warn('Could not write exchange rate cache to Firestore:', err.message);
  }
}

// ─── localStorage cache (guests) ─────────────────────────────────────────────

function loadLocalCache() {
  try {
    const raw = localStorage.getItem(LS_CACHE_KEY);
    if (!raw) return null;
    const { usdRates, fetchedAt } = JSON.parse(raw);
    return Date.now() - fetchedAt < CACHE_TTL_MS ? usdRates : null;
  } catch {
    return null;
  }
}

function saveLocalCache(usdRates) {
  try {
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify({ usdRates, fetchedAt: Date.now() }));
  } catch { /* storage full — ignore */ }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns a usdRates map: { USD: 1, EUR: 1.08, GBP: 1.27, … }
 * meaning "1 unit of currency X = usdRates[X] USD".
 * Reads from cache first; fetches from Yahoo Finance only when stale.
 */
export async function getExchangeRates(uid) {
  // 1. Try Firestore cache (shared, works for logged-in users)
  if (uid) {
    const cached = await loadFirestoreCache();
    if (cached) return cached;
  } else {
    // 2. Try localStorage for guests
    const cached = loadLocalCache();
    if (cached) return cached;
  }

  // 3. Fetch fresh from Yahoo Finance
  const usdRates = await fetchRates();

  // 4. Persist
  if (uid) {
    await saveFirestoreCache(usdRates);
  } else {
    saveLocalCache(usdRates);
  }

  return usdRates;
}

/**
 * Convert an amount between two currencies.
 * Pass usdRates from getExchangeRates().
 * Returns NaN-safe number in toCurrency.
 */
export function convertAmount(amount, fromCurrency, toCurrency, usdRates) {
  const num = parseFloat(amount) || 0;
  if (!usdRates || fromCurrency === toCurrency) return num;
  const fromRate = usdRates[fromCurrency] ?? 1;
  const toRate = usdRates[toCurrency] ?? 1;
  // num * fromRate → USD → / toRate → toCurrency
  return num * fromRate / toRate;
}
